// permissions.ts
// Centralized suite registry + permissions map for Sidebar and route guards
// ─────────────────────────────────────────────────────────────────
// SUITE REGISTRY — add new suites here only. One entry = one CRM.
// ─────────────────────────────────────────────────────────────────

export type UserRole = 'Admin' | 'Manager' | 'Editor' | 'Viewer' | 'Staff' | 'super_admin';
// 'enterprise' and 'wholesale' are the same top-tier plan — support both names
export type PlanName = 'starter' | 'pro' | 'wholesale' | 'enterprise' | 'landlord';

// Business audience type — one entry per suite
export type AudienceType = 'wholesale' | 'landlord' | 'beauty' | 'mechanic' | 'cleaner';

/** Derive audience from the business_type string stored in DB.
 *  The business_type is set at registration and stored as a simple keyword.
 *  Fall back to 'wholesale' (the general business CRM) if unrecognised. */
export function getAudience(businessType: string | null): AudienceType {
  const lower = (businessType || '').toLowerCase();
  if (lower.includes('property') || lower.includes('landlord') || lower.includes('letting')) return 'landlord';
  if (lower.includes('beauty') || lower.includes('salon') || lower.includes('barber'))       return 'beauty';
  if (lower.includes('mechanic') || lower.includes('garage') || lower.includes('mot'))       return 'mechanic';
  if (lower.includes('clean'))                                                                return 'cleaner';
  return 'wholesale';
}

// Sidebar link config — all items shown to all authenticated users
export interface SidebarLink {
  label: string;
  path: string;
  icon: string;
  roles: UserRole[];
  group: string;
  /** If true, rendered as an <a target="_blank"> external link. Sidebar constructs final href. */
  external?: boolean;
}

// Plan feature gating — features not listed here are available on all plans
export interface PlanFeature {
  path: string;          // sidebar path prefix to match
  requiredPlan: PlanName;
  featureName: string;
  featureDescription: string;
  requiredPlanLabel: string;
}

const PLAN_RANK: Record<PlanName, number> = { starter: 1, pro: 2, wholesale: 3, enterprise: 3, landlord: 3 };

/** Returns true if the user's plan meets the minimum required plan */
export function isPlanSufficient(userPlan: string | null, requiredPlan: PlanName): boolean {
  const normalized = (userPlan || 'starter').toLowerCase() as PlanName;
  const userRank = PLAN_RANK[normalized] ?? 3;
  return userRank >= PLAN_RANK[requiredPlan];
}

// All sidebar items — shown to all authenticated users regardless of role or plan.
// Plan locks are enforced by Sidebar.tsx (modal instead of navigate).
// Route protection for role-sensitive pages is done by ProtectedRoute.
const ALL_ROLES: UserRole[] = ['Admin', 'Manager', 'Editor', 'Viewer', 'Staff'];
const ADMIN_ONLY: UserRole[] = ['Admin'];
const ADMIN_MANAGER: UserRole[] = ['Admin', 'Manager'];
const ADMIN_EDITOR: UserRole[] = ['Admin', 'Editor'];
const ADMIN_MANAGER_EDITOR: UserRole[] = ['Admin', 'Manager', 'Editor'];
const ADMIN_MANAGER_EDITOR_VIEWER: UserRole[] = ['Admin', 'Manager', 'Editor', 'Viewer'];
const ADMIN_MANAGER_VIEWER_STAFF: UserRole[] = ['Admin', 'Manager', 'Viewer', 'Staff'];
const ADMIN_MANAGER_EDITOR_STAFF: UserRole[] = ['Admin', 'Manager', 'Editor', 'Staff'];
const ADMIN_VIEWER: UserRole[] = ['Admin', 'Viewer'];

// ─────────────────────────────────────────────────────────────────
// WHOLESALE-RETAIL SUITE (formerly "Standard Business")
// Cash and carry, retail shops, wholesale distributors
// ─────────────────────────────────────────────────────────────────
export const WHOLESALE_SIDEBAR_LINKS: SidebarLink[] = [
  // ── Main ──
  { label: 'Dashboard',    path: '/dashboard',         icon: '🏠', group: 'Main',          roles: ALL_ROLES },
  { label: 'Customers',    path: '/customers',         icon: '👤', group: 'Main',          roles: ADMIN_MANAGER_EDITOR_STAFF },
  // ── Inventory ──
  { label: 'Products',     path: '/products',          icon: '📦', group: 'Inventory',     roles: ADMIN_MANAGER_EDITOR_VIEWER },
  { label: 'Suppliers',        path: '/suppliers',         icon: '🏭', group: 'Inventory',     roles: ADMIN_MANAGER },
  { label: 'Categories',       path: '/categories',        icon: '🗂️', group: 'Inventory',     roles: ADMIN_MANAGER_EDITOR_VIEWER },
  { label: 'Purchase Orders',  path: '/purchase-orders',   icon: '📋', group: 'Inventory',     roles: ADMIN_MANAGER },
  { label: 'Stock Movements',  path: '/stock-movements',   icon: '🔄', group: 'Inventory',     roles: ADMIN_MANAGER_EDITOR_VIEWER },
  // ── Sales ──
  { label: 'Orders',       path: '/orders',            icon: '🛒', group: 'Sales',         roles: ADMIN_MANAGER_EDITOR_VIEWER },
  { label: 'Invoices',     path: '/invoices/list',     icon: '🧾', group: 'Sales',         roles: ADMIN_MANAGER_EDITOR_VIEWER },
  { label: 'Credit Notes', path: '/credit-notes',      icon: '🎫', group: 'Sales',         roles: ADMIN_EDITOR },
  // ── Finance ──
  { label: 'Accounting',   path: '/accounting',        icon: '💰', group: 'Finance',       roles: ADMIN_VIEWER },
  { label: 'Analytics',    path: '/analytics',         icon: '📊', group: 'Finance',       roles: ALL_ROLES },
  // ── Communication ──
  { label: 'Booking',      path: '/booking',           icon: '📅', group: 'Communication', roles: ADMIN_MANAGER_VIEWER_STAFF },
  { label: 'Online Shop',  path: '/shop',              icon: '🛍️', group: 'Communication', roles: ALL_ROLES, external: true },
  { label: 'Send Message', path: '/send-message',      icon: '📨', group: 'Communication', roles: ADMIN_MANAGER_EDITOR },
  { label: 'Support',      path: '/support',           icon: '🆘', group: 'Communication', roles: ALL_ROLES },
  // ── System ──
  { label: 'Settings',     path: '/business-settings', icon: '⚙️', group: 'System',        roles: ADMIN_ONLY },
  { label: 'Staff',        path: '/staff',             icon: '🧑‍💼', group: 'System',        roles: ADMIN_MANAGER },
  { label: 'Audit Log',    path: '/audit-log',         icon: '📋', group: 'System',        roles: ADMIN_ONLY },
];

// Keep legacy export name so existing Sidebar.tsx imports don't break
export const SIDEBAR_LINKS = WHOLESALE_SIDEBAR_LINKS;

export const WHOLESALE_PLAN_FEATURES: PlanFeature[] = [
  { path: '/orders',        requiredPlan: 'pro',       featureName: 'Orders',        featureDescription: 'Track every job from quote to invoice and never lose a customer project again.',           requiredPlanLabel: 'Pro and above' },
  { path: '/credit-notes',  requiredPlan: 'pro',       featureName: 'Credit Notes',  featureDescription: 'Issue refunds and credit adjustments to customers in a professional format.',             requiredPlanLabel: 'Pro and above' },
  { path: '/suppliers',     requiredPlan: 'pro',       featureName: 'Suppliers',     featureDescription: 'Manage your supplier relationships, contacts and purchase orders in one place.',           requiredPlanLabel: 'Pro and above' },
  { path: '/categories',    requiredPlan: 'pro',       featureName: 'Categories',    featureDescription: 'Organise your products and services into clear, searchable categories.',                   requiredPlanLabel: 'Pro and above' },
  { path: '/accounting',    requiredPlan: 'pro',       featureName: 'Accounting',    featureDescription: 'Full accounts receivable, aging reports and a financial overview of your business.',       requiredPlanLabel: 'Pro and above' },
  { path: '/analytics',     requiredPlan: 'pro',       featureName: 'Analytics',     featureDescription: 'Revenue trends, booking performance, customer insights and growth data.',                  requiredPlanLabel: 'Pro and above' },
  { path: '/send-message',  requiredPlan: 'pro',       featureName: 'Send Message',  featureDescription: 'Send SMS and email messages directly to your customers — reminders, promotions and more.', requiredPlanLabel: 'Pro and above' },
  { path: '/audit-log',     requiredPlan: 'wholesale', featureName: 'Audit Log',     featureDescription: 'Full history of every action taken in your account — who did what and when.',             requiredPlanLabel: 'Wholesale plan' },
  { path: '/shop',          requiredPlan: 'wholesale', featureName: 'Online Shop',   featureDescription: 'A public-facing wholesale portal with B2B price tiers and bulk order management.',      requiredPlanLabel: 'Wholesale plan' },
];

// Keep legacy export name so existing code doesn't break
export const PLAN_FEATURES = WHOLESALE_PLAN_FEATURES;

/** Online Shop is plan-gated separately since it renders as an external link */
export const ONLINE_SHOP_FEATURE: PlanFeature = {
  path: '/shop',
  requiredPlan: 'wholesale',
  featureName: 'Online Shop',
  featureDescription: 'A public-facing wholesale portal with B2B price tiers and bulk order management.',
  requiredPlanLabel: 'Wholesale plan',
};

/** Returns the PlanFeature if the given path matches a locked feature, else undefined */
export function getLockedFeature(path: string): PlanFeature | undefined {
  return PLAN_FEATURES.find(f => path.startsWith(f.path));
}

// ─────────────────────────────────────────────────────────────────
// LANDLORD SUITE — property management, lettings, HMO
// ─────────────────────────────────────────────────────────────────
export const LANDLORD_SIDEBAR_LINKS: SidebarLink[] = [
  // ── Main ──
  { label: 'Dashboard',              path: '/landlord/home',          icon: '🏠', group: 'Main',        roles: ALL_ROLES },
  // ── Portfolio ──
  { label: 'Properties',             path: '/landlord/properties',    icon: '🏘️', group: 'Portfolio',   roles: ALL_ROLES },
  { label: 'Tenants',                path: '/landlord/tenants',       icon: '👤', group: 'Portfolio',   roles: ALL_ROLES },
  { label: 'Leases',                 path: '/landlord/leases',        icon: '📄', group: 'Portfolio',   roles: ALL_ROLES },
  { label: 'Sales & Buy',            path: '/landlord/pipeline',      icon: '🏗️', group: 'Portfolio',   roles: ALL_ROLES },
  // ── Money ──
  { label: 'Rent Tracker',           path: '/landlord/rent',          icon: '💷', group: 'Money',       roles: ALL_ROLES },
  { label: 'Accounting',             path: '/landlord/accounting',    icon: '💰', group: 'Money',       roles: ALL_ROLES },
  // ── Compliance ──
  { label: 'Maintenance',            path: '/landlord/maintenance',   icon: '🛠️', group: 'Compliance',  roles: ALL_ROLES },
  { label: 'Docs & Certs',           path: '/landlord/certificates',  icon: '📁', group: 'Compliance',  roles: ALL_ROLES },
  { label: 'Viewings',               path: '/landlord/viewings',      icon: '📅', group: 'Compliance',  roles: ALL_ROLES },
  { label: 'Inventory',              path: '/landlord/inventory',     icon: '📦', group: 'Compliance',  roles: ALL_ROLES },
  // ── Team ──
  { label: 'Staff',                  path: '/staff',                  icon: '🧑‍💼', group: 'Team',        roles: ADMIN_MANAGER },
  { label: 'Support',                path: '/support',                icon: '🎧', group: 'Team',          roles: ALL_ROLES },
  { label: 'Messages',               path: '/send-message',           icon: '📨', group: 'Team',          roles: ADMIN_MANAGER_EDITOR },
  // ── System ──
  { label: 'Settings',               path: '/business-settings',      icon: '⚙️', group: 'System',      roles: ADMIN_ONLY },
  { label: 'Audit Log',              path: '/landlord/audit-log',     icon: '📋', group: 'System',      roles: ALL_ROLES },
];

export const LANDLORD_PLAN_FEATURES: PlanFeature[] = [
  { path: '/invoices',            requiredPlan: 'pro',        featureName: 'Invoices',     featureDescription: 'Send professional invoices directly to your tenants.',                          requiredPlanLabel: 'Pro and above' },
  { path: '/credit-notes',        requiredPlan: 'pro',        featureName: 'Credit Notes', featureDescription: 'Issue credit adjustments and refunds to tenants in a professional format.',     requiredPlanLabel: 'Pro and above' },
  { path: '/landlord/accounting', requiredPlan: 'pro',        featureName: 'Accounting',   featureDescription: 'Full financial overview, income and expense tracking per property.',           requiredPlanLabel: 'Pro and above' },
  { path: '/analytics',           requiredPlan: 'pro',        featureName: 'Analytics',    featureDescription: 'Revenue trends, occupancy rates and property performance data.',               requiredPlanLabel: 'Pro and above' },
  { path: '/send-message',        requiredPlan: 'pro',        featureName: 'Messages',     featureDescription: 'Send SMS and email messages directly to your tenants.',                        requiredPlanLabel: 'Pro and above' },
  { path: '/staff',               requiredPlan: 'pro',        featureName: 'Staff',        featureDescription: 'Invite and manage team members — agents, accountants, maintenance staff.',     requiredPlanLabel: 'Pro and above' },
  { path: '/landlord/audit-log',   requiredPlan: 'enterprise', featureName: 'Audit Log',    featureDescription: 'Full history of every action taken in your account — who did what and when.', requiredPlanLabel: 'Enterprise plan' },
];

// ─────────────────────────────────────────────────────────────────
// BEAUTY SUITE — salons, barbers, beauty therapists (coming Wave 4)
// ─────────────────────────────────────────────────────────────────
export const BEAUTY_SIDEBAR_LINKS: SidebarLink[] = [
  { label: 'Dashboard',    path: '/beauty/dashboard',  icon: '🏠', group: 'Main',          roles: ALL_ROLES },
  { label: 'Clients',      path: '/customers',         icon: '👤', group: 'Clients',       roles: ALL_ROLES },
  { label: 'Appointments', path: '/beauty/bookings',   icon: '📅', group: 'Appointments',  roles: ALL_ROLES },
  { label: 'Services',     path: '/products',          icon: '💈', group: 'Appointments',  roles: ALL_ROLES },
  { label: 'Staff Rota',   path: '/staff',             icon: '🧑‍💼', group: 'Team',          roles: ALL_ROLES },
  { label: 'POS / Sales',  path: '/orders/list',       icon: '💳', group: 'Finance',       roles: ALL_ROLES },
  { label: 'Accounting',   path: '/accounting',        icon: '💰', group: 'Finance',       roles: ALL_ROLES },
  { label: 'Stock',        path: '/products',          icon: '📦', group: 'Stock',         roles: ALL_ROLES },
  { label: 'Support',      path: '/support',           icon: '🎧', group: 'Communication', roles: ALL_ROLES },
  { label: 'Settings',     path: '/business-settings', icon: '⚙️', group: 'System',        roles: ALL_ROLES },
];

export const BEAUTY_PLAN_FEATURES: PlanFeature[] = [
  { path: '/beauty/accounting', requiredPlan: 'pro', featureName: 'Accounting', featureDescription: 'Full financial overview for your salon.', requiredPlanLabel: 'Pro and above' },
  { path: '/beauty/stock',      requiredPlan: 'pro', featureName: 'Stock',      featureDescription: 'Track product stock and salon supplies.',  requiredPlanLabel: 'Pro and above' },
];

// ─────────────────────────────────────────────────────────────────
// MECHANIC SUITE — garages, MOT centres, auto repair (coming Wave 4)
// ─────────────────────────────────────────────────────────────────
export const MECHANIC_SIDEBAR_LINKS: SidebarLink[] = [
  { label: 'Dashboard',    path: '/dashboard',            icon: '🏠', group: 'Main',       roles: ALL_ROLES },
  { label: 'Vehicles',     path: '/mechanic/vehicles',    icon: '🚗', group: 'Jobs',       roles: ALL_ROLES },
  { label: 'Job Cards',    path: '/mechanic/jobs',        icon: '🔧', group: 'Jobs',       roles: ALL_ROLES },
  { label: 'Parts',        path: '/mechanic/parts',       icon: '⚙️', group: 'Parts',      roles: ALL_ROLES },
  { label: 'Invoices',     path: '/mechanic/invoices',    icon: '🧾', group: 'Finance',    roles: ALL_ROLES },
  { label: 'Accounting',   path: '/mechanic/accounting',  icon: '💰', group: 'Finance',    roles: ALL_ROLES },
  { label: 'MOT Reminders',path: '/mechanic/mot',         icon: '📅', group: 'Reminders',  roles: ALL_ROLES },
  { label: 'Support',      path: '/support',              icon: '🎧', group: 'Communication', roles: ALL_ROLES },
  { label: 'Settings',     path: '/business-settings',    icon: '⚙️', group: 'System',     roles: ALL_ROLES },
];

export const MECHANIC_PLAN_FEATURES: PlanFeature[] = [
  { path: '/mechanic/accounting', requiredPlan: 'pro', featureName: 'Accounting', featureDescription: 'Full financial overview for your garage.', requiredPlanLabel: 'Pro and above' },
];

// ─────────────────────────────────────────────────────────────────
// CLEANER SUITE — domestic and commercial cleaning companies (coming Wave 4)
// ─────────────────────────────────────────────────────────────────
export const CLEANER_SIDEBAR_LINKS: SidebarLink[] = [
  { label: 'Dashboard',   path: '/dashboard',           icon: '🏠', group: 'Main',       roles: ALL_ROLES },
  { label: 'Clients',     path: '/cleaner/clients',     icon: '👤', group: 'Clients',    roles: ALL_ROLES },
  { label: 'Jobs',        path: '/cleaner/jobs',        icon: '🧹', group: 'Jobs',       roles: ALL_ROLES },
  { label: 'Schedule',    path: '/cleaner/schedule',    icon: '📅', group: 'Jobs',       roles: ALL_ROLES },
  { label: 'Staff',       path: '/cleaner/staff',       icon: '🧑‍💼', group: 'Team',       roles: ALL_ROLES },
  { label: 'Invoices',    path: '/cleaner/invoices',    icon: '🧾', group: 'Finance',    roles: ALL_ROLES },
  { label: 'Accounting',  path: '/cleaner/accounting',  icon: '💰', group: 'Finance',    roles: ALL_ROLES },
  { label: 'Support',     path: '/support',             icon: '🎧', group: 'Communication', roles: ALL_ROLES },
  { label: 'Settings',    path: '/business-settings',   icon: '⚙️', group: 'System',     roles: ALL_ROLES },
];

export const CLEANER_PLAN_FEATURES: PlanFeature[] = [
  { path: '/cleaner/accounting', requiredPlan: 'pro', featureName: 'Accounting', featureDescription: 'Full financial overview for your cleaning business.', requiredPlanLabel: 'Pro and above' },
];

// ─────────────────────────────────────────────────────────────────
// SUITE REGISTRY — the single source of truth
// To add a new suite: add one entry here + create its pages folder + backend route file
// ─────────────────────────────────────────────────────────────────
export interface Suite {
  name: string;
  sidebar: SidebarLink[];
  planFeatures: PlanFeature[];
}

export const SUITES: Record<AudienceType, Suite> = {
  wholesale: { name: 'Wholesale-Retail Suite', sidebar: WHOLESALE_SIDEBAR_LINKS, planFeatures: WHOLESALE_PLAN_FEATURES },
  landlord:  { name: 'Landlord Suite',         sidebar: LANDLORD_SIDEBAR_LINKS,  planFeatures: LANDLORD_PLAN_FEATURES  },
  beauty:    { name: 'Beauty Suite',           sidebar: BEAUTY_SIDEBAR_LINKS,    planFeatures: BEAUTY_PLAN_FEATURES    },
  mechanic:  { name: 'Mechanic Suite',         sidebar: MECHANIC_SIDEBAR_LINKS,  planFeatures: MECHANIC_PLAN_FEATURES  },
  cleaner:   { name: 'Cleaning Suite',         sidebar: CLEANER_SIDEBAR_LINKS,   planFeatures: CLEANER_PLAN_FEATURES   },
};

/** Return the correct sidebar links based on business type */
export function getSidebarLinks(businessType: string | null): SidebarLink[] {
  return SUITES[getAudience(businessType)].sidebar;
}

/** Return the correct plan features based on business type */
export function getPlanFeatures(businessType: string | null): PlanFeature[] {
  return SUITES[getAudience(businessType)].planFeatures;
}

/** Return the suite name for display */
export function getSuiteName(businessType: string | null): string {
  return SUITES[getAudience(businessType)].name;
}
